import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, Button, Select, Typography, message } from 'antd';
import { LockOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useDocumentTitle from '../hooks/useDocumentTitle';
import UserService from '../services/user';
import RoleService from '../services/role';
import { optionService } from '../services/option';
import CUSTOMER_CATEGORY_OPTIONS from '../config/customer-categories';

const { Option } = Select;
const { Title } = Typography;

/**
 * UserCreate: 新建用户页面（每个表单一行，最大宽度900px）。
 */
const UserCreate: React.FC = () => {
  useDocumentTitle('添加用户 - 用户');
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [parentCandidates, setParentCandidates] = useState<any[]>([]);
  const [clientTypeOptions, setClientTypeOptions] = useState<string[]>([]);
  const [sourceOptions, setSourceOptions] = useState<string[]>([]);

  const watchedRoles = Form.useWatch('roles', form) as any;
  const rolesArray = useMemo(() => (Array.isArray(watchedRoles) ? watchedRoles : watchedRoles ? [watchedRoles] : []), [watchedRoles]);
  const isCustomerRole = useMemo(() => {
    const aliases = ['customer', 'client', 'user', '客户'];
    return rolesArray.some((r: any) => aliases.includes(String(r)));
  }, [rolesArray]);
  const canSelectParent = useMemo(() => {
    const names = rolesArray.map((r: any) => String(r));
    // 销售经理、协调经理不需要设置上级
    if (names.includes('sales_manager') || names.includes('coordinator_manager')) {
      return false;
    }
    return names.length > 0 && !names.includes('client') && !names.includes('viewer') && !names.includes('admin');
  }, [rolesArray]);

  const filteredParentCandidates = useMemo(() => {
    const names = rolesArray.map((r: any) => String(r));
    // sales 的上级用户从 sales_manager 中选择
    if (names.includes('sales')) {
      return parentCandidates.filter((u: any) => Array.isArray(u.roles) && u.roles.some((r: any) => r?.name === 'sales_manager'));
    }
    // coordinator 的上级用户从 coordinator_manager 中选择
    if (names.includes('coordinator')) {
      return parentCandidates.filter((u: any) => Array.isArray(u.roles) && u.roles.some((r: any) => r?.name === 'coordinator_manager'));
    }
    return parentCandidates;
  }, [rolesArray, parentCandidates]);

  useEffect(() => {
    const load = async () => {
      try {
        const [rolesRes, usersRes] = await Promise.all([
          RoleService.getRoles(),
          UserService.getUsers(),
        ]);
        if (rolesRes.success && Array.isArray(rolesRes.data)) setRoles(rolesRes.data);
        if (usersRes.success && Array.isArray(usersRes.data)) setParentCandidates(usersRes.data);
      } catch {}
    };
    load();
  }, []);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [clientTypes, sources] = await Promise.all([
          optionService.get('customer_types', []),
          optionService.get('customer_sources', []),
        ]);
        setClientTypeOptions(Array.isArray(clientTypes) ? clientTypes : []);
        setSourceOptions(Array.isArray(sources) ? sources : []);
      } catch {}
    };
    loadOptions();
  }, []);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const payloadRoles = Array.isArray(values.roles) ? values.roles : values.roles ? [values.roles] : [];
      const createPayload: any = {
        username: values.username,
        email: values.email,
        password: values.password,
        roles: payloadRoles,
        parent_id: typeof values.parent_id === 'undefined' ? undefined : values.parent_id || null,
        phone: values.phone,
        contact_name: values.contact_name,
        client_type: values.client_type,
        customer_categories: values.customer_categories,
        source: values.source,
      };

      const createResp = await UserService.createUser(createPayload);
      if (createResp.success && createResp.data) {
        message.success('用户添加成功');
        navigate(`/profile?userId=${(createResp.data as any).id}`);
        return;
      }
      message.error(createResp.message || '创建失败');
    } catch (error: any) {
      message.error(error?.response?.data?.message || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <Title level={3}>添加用户</Title>
        <Card>
          <Form
            className="user-create-form"
            form={form}
            layout="horizontal"
            labelAlign="right"
            labelCol={{ flex: '120px' }}
            wrapperCol={{ flex: 'auto' }}
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item name="username" label="用户名" rules={[{ required: true, message: '请输入用户名' }, { min: 3, message: '用户名至少3个字符' }, { max: 50, message: '用户名不能超过50个字符' }]}>
              <Input prefix={<UserOutlined />} placeholder="请输入用户名" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="email" label="邮箱" rules={[{ required: true, message: '请输入邮箱' }, { type: 'email', message: '请输入有效邮箱' }]}>
              <Input placeholder="请输入邮箱" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }, { min: 8, message: '密码至少8个字符' }]}>
              <Input.Password prefix={<LockOutlined />} placeholder="请输入密码" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="phone" label="电话" rules={[{ required: true, message: '请输入电话号码' }, { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' }]}>
              <Input prefix={<PhoneOutlined />} placeholder="请输入手机号码" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="contact_name" label="客户名称">
              <Input placeholder="请输入对接人/用户姓名" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="roles" label="用户角色" rules={[{ required: true, message: '请选择一个角色' }]}>
              <Select placeholder="请选择角色" allowClear showSearch style={{ width: '100%' }}>
                {roles.map((role: any) => (
                  <Option key={role.id} value={role.name}>
                    {role.display_name || role.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {isCustomerRole && (
              <Form.Item name="client_type" label="客户等级">
                <Select placeholder="请选择客户等级" allowClear showSearch style={{ width: '100%' }}>
                  {clientTypeOptions.map((opt) => (
                    <Option key={opt} value={opt}>
                      {opt}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {isCustomerRole && (
              <Form.Item name="customer_categories" label="客户分类">
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder="请选择客户分类"
                  style={{ width: '100%' }}
                  options={CUSTOMER_CATEGORY_OPTIONS.map(v => ({ value: v, label: v }))}
                />
              </Form.Item>
            )}

            {isCustomerRole && (
              <Form.Item name="source" label="客户来源">
                <Select placeholder="请选择客户来源" allowClear showSearch style={{ width: '100%' }}>
                  {sourceOptions.map((opt) => (
                    <Option key={opt} value={opt}>
                      {opt}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {canSelectParent && (
              <Form.Item name="parent_id" label="选择上级用户">
                <Select placeholder="请选择上级用户（可选）" allowClear showSearch optionFilterProp="children" style={{ width: '100%' }}>
                  {filteredParentCandidates.map((u: any) => (
                    <Option key={u.id} value={u.id}>
                      {u.display_name || u.name} ({u.email})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item style={{paddingLeft: 120}}>
              <Button type="primary" htmlType="submit" loading={loading}>
                添加用户
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default UserCreate;
